import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { VStack } from '@cbhq/cds-web/layout';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { TextHeadline, TextBody } from '@cbhq/cds-web/typography';
import { Chain, extractChain } from 'viem';
import * as chains from 'wagmi/chains';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { ModalFooter } from '@cbhq/cds-web/overlays/modal/ModalFooter';
import { useChainSwitch } from '@/components/ChainSwitch/ChainSwitchContext';

export function ChainSwitchModal() {
  const {
    forceSwitchChainId,
    currentChainId,
    supportedChains,
    handleSwitchChain,
    handleModalClose,
    chainSwitchModalVisible,
  } = useChainSwitch();

  // Determine current chain info for display
  const currentChain = currentChainId
    ? extractChain({
        chains: Object.values(chains) as readonly Chain[],
        id: currentChainId,
      })
    : undefined;

  // Get target chain from forceSwitchChainId
  const targetChain = supportedChains.find((chain) => chain.id === forceSwitchChainId);

  const handleConfirmClick = useCallback(() => {
    if (targetChain) {
      void handleSwitchChain({ chainId: targetChain.id });
    }
  }, [targetChain, handleSwitchChain]);

  // No-op function for onRequestClose since we can't close forced switch
  const handleRequestClose = useCallback(() => {}, []);

  const { isPhone, isPhoneLandscape } = useBreakpoints();

  const description = useMemo(
    () => (
      <>
        <TextHeadline as="span">{currentChain?.name}</TextHeadline> is not supported. Please switch
        to <TextHeadline as="span">{targetChain?.name}</TextHeadline> to continue.
      </>
    ),
    [currentChain, targetChain],
  );

  useEffect(() => {
    // guardrail to prevent modal from showing if we're trying to switch to the current chain
    if (chainSwitchModalVisible && currentChainId === forceSwitchChainId) {
      handleModalClose();
    }
  }, [chainSwitchModalVisible, currentChainId, forceSwitchChainId, handleModalClose]);

  return (
    <Modal
      width={isPhone || isPhoneLandscape ? undefined : 422}
      visible={chainSwitchModalVisible}
      onRequestClose={handleRequestClose}
      shouldCloseOnEscPress={false}
      hideDividers
      testID="chain-switch-modal"
      // ensure the TOS modal is on top of the chain switch modal
      zIndex={999}
    >
      <ModalHeader title="Unsupported Network" />
      <ModalBody>
        <VStack alignItems="center" justifyContent="center" width="100%" paddingBottom={2}>
          <TextBody as="p" testID="chain-switch-modal-description">
            {description}
          </TextBody>
        </VStack>
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button
            variant="primary"
            onClick={handleConfirmClick}
            testID="chain-switch-modal-confirm-button"
            block
          >
            {`Switch to ${targetChain?.name}`}
          </Button>
        }
      />
    </Modal>
  );
}
